import { useEffect, useState } from "react";
import {
  FoodItemEffectiveTags,
  MenuItemFoodTagOverrideCreate,
} from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import { isApiError, toJspApiError } from "models/utils/apiUtils";
import {
  TagOverrides,
  toOverrideCreate,
} from "models/menu/menuItemTagOverride";

export interface UseFoodEffectiveTagsArgs {
  foodId: number;
  /** Site whose supply profiles drive the enrichment; null sends none. */
  siteId: number | null;
  overrides: TagOverrides;
  /** False while the preview is closed: no request, state reset. */
  enabled: boolean;
}

export interface FoodEffectiveTagsState {
  data: FoodItemEffectiveTags | null;
  isLoading: boolean;
  error: string | null;
}

const IDLE: FoodEffectiveTagsState = {
  data: null,
  isLoading: false,
  error: null,
};

/** User-facing message for a failed preview call. */
export const effectiveTagsErrorMessage = (err: unknown): string => {
  if (isApiError(err)) {
    const { status, body } = toJspApiError(err);
    if (status === 403) {
      return "This site is not available to your account.";
    }
    if (status === 422) {
      const detail = (body as { detail?: unknown } | undefined)?.detail;
      if (typeof detail === "string") {
        return detail;
      }
      if (Array.isArray(detail)) {
        const first = (detail as { msg?: unknown }[])[0];
        if (typeof first?.msg === "string") {
          return first.msg;
        }
      }
      return "The server rejected this override.";
    }
  }
  return "Could not load tags.";
};

/**
 * Preview what a menu item on `foodId` reports with the given overrides
 * applied (POST /food/item/{food_id}/tags). Runs only while `enabled`; a
 * response for a superseded request is dropped.
 */
export const useFoodEffectiveTags = ({
  foodId,
  siteId,
  overrides,
  enabled,
}: UseFoodEffectiveTagsArgs): FoodEffectiveTagsState => {
  const [state, setState] = useState<FoodEffectiveTagsState>(IDLE);
  // Serialise so the effect re-runs only when the records actually change.
  const overridesKey = JSON.stringify((overrides ?? []).map(toOverrideCreate));

  useEffect(() => {
    if (!enabled || foodId <= 0) {
      setState(IDLE);
      return undefined;
    }
    let cancelled = false;
    setState({ data: null, isLoading: true, error: null });
    jspPlatformApi()
      .foodItem.postFoodItemByFoodIdTags({
        path: { food_id: foodId },
        query: siteId == null ? undefined : { for_site_id: siteId },
        body: {
          food_tag_overrides: JSON.parse(
            overridesKey,
          ) as MenuItemFoodTagOverrideCreate[],
        },
        throwOnError: true,
        // The preview shows the failure inline; no global snackbar.
        suppressErrorPrompt: true,
      })
      .then((res) => {
        if (!cancelled) {
          setState({ data: res.data ?? null, isLoading: false, error: null });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            data: null,
            isLoading: false,
            error: effectiveTagsErrorMessage(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, foodId, siteId, overridesKey]);

  return state;
};

export default useFoodEffectiveTags;
